import { Badge, cn } from '@design-system';
import {
  CarePhaseDataItem,
  VerticalPhaseCard,
  FullCareSlideOutContent,
  EarlyCareSlideOutContent,
  ModerateCareSlideOutContent,
} from '@/components';
import {
  OnboardingLayout,
  useSlideSetup,
  useCarePhasesSlide,
  CustomizePlanButtonGroup,
  type CarePhaseComputedData,
} from '@/features/client-onboarding';
import { OnboardingCostFactors } from '@/pages/OnboardingCostFactors';

import { ClockIcon, HandHelping, Accessibility, HeartPulse } from 'lucide-react';

import { possessive,
  CarePhase,
  formatInteger,
  carePhaseDefList,
  isDurationCustom,
  formatDurationYears,
} from '@shared';

import type { FunctionComponent } from 'react';

interface CarePhaseProps {
  carePhase: CarePhase;
  slidePage: FunctionComponent<ClientIdProps>;
  title?: string;
}

const CARE_PHASE_CONFIGS: Omit<CarePhaseDataItem, Exclude<keyof CarePhaseComputedData, 'value'>>[] =
  [
    {
      value: CarePhase.earlyCare,
      carePhase: carePhaseDefList[0].label,
      adlRange: '1-2',
      careSettingOptions: 'Home and Independent Living',
      adlRangeIcon: <HandHelping className="size-5" />,
      slideOutContent: <EarlyCareSlideOutContent />,
    },
    {
      value: CarePhase.moderateCare,
      carePhase: carePhaseDefList[1].label,
      adlRange: '3-4',
      careSettingOptions: 'Home and Assisted Living',
      adlRangeIcon: <Accessibility className="size-5" />,
      slideOutContent: <ModerateCareSlideOutContent />,
    },
    {
      value: CarePhase.fullCare,
      carePhase: carePhaseDefList[2].label,
      adlRange: '5-6',
      careSettingOptions: 'Home and Nursing Home',
      adlRangeIcon: <HeartPulse className="size-5" />,
      slideOutContent: <FullCareSlideOutContent />,
    },
  ];

interface CareTimelineDurationProps {
  clientHasStartedLtc: boolean;
  ltcDurationYears: number;
  phaseCosts: SinglePhaseCosts[];
}

function CareTimelineDuration({
  clientHasStartedLtc,
  ltcDurationYears,
  phaseCosts,
}: CareTimelineDurationProps) {
  const isCustomDuration = isDurationCustom(phaseCosts);

  return (
    <Badge
      variant="outline"
      className={cn('text-sm font-normal', {
        'pr-0.5!': isCustomDuration,
      })}
    >
      <ClockIcon className="size-4!" />
      <span className="flex gap-1">
        <p className="w-max">{`Estimated ${clientHasStartedLtc ? 'remaining ' : ''}care duration`}</p>
        <p className="text-primary w-max font-medium">
          {`${formatDurationYears(ltcDurationYears)} years (${formatInteger(
            ltcDurationYears * 12,
          )} months)`}
          {isCustomDuration && (
            <Badge
              variant="secondary"
              className="ml-2"
            >
              Edited
            </Badge>
          )}
        </p>
      </span>
    </Badge>
  );
}

export function OnboardingCarePhases({ carePhase, slidePage, title }: CarePhaseProps) {
  const {
    client,
    clientId,
    clientFirstName,
    clientHasStartedLtc,
    clientCurrentCarePhase,
    ltcDurationYears,
    phaseCosts,
    carePhaseComputedData,
    visibleIndices,
    visibleLineIndices,
    nextButtonLabel,
  } = useCarePhasesSlide(carePhase);

  const carePhaseData = CARE_PHASE_CONFIGS.map((config, index) => ({
    ...config,
    ...carePhaseComputedData[index],
  }));

  useSlideSetup({
    slidePage,
    nextButtonLabel,
    previousSlidePage: OnboardingCostFactors,
  });

  return (
    <OnboardingLayout>
      <div className="flex flex-col justify-center gap-6 py-6 md:py-16">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-6 gap-y-4">
          <div className="space-y-2">
            <h1 className="max-w-xl text-balance text-2xl font-bold text-gray-950 md:text-4xl">
              {title ?? `${possessive(clientFirstName)} care timeline`}
            </h1>
            <CareTimelineDuration
              clientHasStartedLtc={clientHasStartedLtc}
              ltcDurationYears={ltcDurationYears}
              phaseCosts={phaseCosts}
            />
          </div>
          <CustomizePlanButtonGroup client={client} />
        </div>

        {/* Vertical phase cards layout */}
        <div className="w-full max-w-2xl">
          {carePhaseData.map((phase, index) => {
            const isLastPhase = index === carePhaseData.length - 1;
            const phaseNumber = index + 1;

            return (
              <VerticalPhaseCard
                key={phase.carePhase}
                phase={phase}
                phaseNumber={phaseNumber}
                isLastPhase={isLastPhase}
                isVisible={visibleIndices.includes(index)}
                isLineVisible={visibleLineIndices.includes(index)}
                carePhase={carePhase}
                clientId={clientId}
                clientHasStartedLtc={clientHasStartedLtc}
                clientCurrentCarePhase={clientCurrentCarePhase}
                timelineText={phase.timelineText}
                timelineTooltip={phase.timelineTooltip}
              />
            );
          })}
        </div>
      </div>
    </OnboardingLayout>
  );
}

export function OnboardingCarePhasesPhaseOne({ clientId }: ClientIdProps) {
  return (
    <OnboardingCarePhases
      carePhase={CarePhase.earlyCare}
      slidePage={OnboardingCarePhasesPhaseOne}
    />
  );
}
